import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import client from '@/apolloClient';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import DatePickerField from '@/components/fields/DatePickerField';
import SelectField from '@/components/fields/SelectField';
import SwitchField from '@/components/fields/SwitchField';
import { splitSeconds } from '@/utils/common';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';
import type { AssessmentRunOption } from '../../shared/utils';
import FinSwimInnerForm from '../FinSwimInnerForm';
import { baseFinSwimPerformanceFormSchema, type EditFinSwimPerformanceFormData } from '../FinSwimPerformanceFormConfig';
import type { FinSwimPerformanceRowData } from '../FinSwimPerformanceTab';
import { UpdatePerformanceFinSwimResultMutation } from './UpdatePerformanceFinSwimResult.graphql';

interface EditPerformanceFinSwimModalProps {
    open: boolean;
    onClose: (refresh?: boolean) => void;
    rowData: FinSwimPerformanceRowData | null;
    assessmentRunOptions: AssessmentRunOption[];
}

const EditPerformanceFinSwimModal = ({
    open,
    onClose,
    rowData,
    assessmentRunOptions,
}: EditPerformanceFinSwimModalProps) => {
    const { t } = useTranslation('performanceResult');

    const { minutes: completionTimeMinutes, seconds: completionTimeSeconds } = splitSeconds(rowData?.completionTime);

    const form = useForm<EditFinSwimPerformanceFormData>({
        mode: 'onChange',
        resolver: zodResolver(baseFinSwimPerformanceFormSchema),
        defaultValues: {
            completionTime: rowData?.completionTime ?? undefined,
            date: rowData?.assessmentDate ?? undefined,
            isTiedToAssessmentRun: rowData?.isTiedToAssessmentRun ?? false,
            assessmentRunId: rowData?.assessmentRunId ?? undefined,
            completionTimeMinutes,
            completionTimeSeconds,
        },
    });

    const isTiedToAssessmentRun = form.watch('isTiedToAssessmentRun');

    const handleSubmit = async () => {
        if (!(await form.trigger()) || !rowData) {
            return;
        }

        const values = form.getValues();

        try {
            const { data } = await client.mutate({
                mutation: UpdatePerformanceFinSwimResultMutation,
                variables: {
                    id: rowData.id,
                    input: {
                        assessmentRunId: values.isTiedToAssessmentRun ? values.assessmentRunId || undefined : undefined,
                        result: {
                            timingSec: values.completionTime ?? undefined,
                        },
                        completedAt: values.date,
                    },
                },
            });

            if (data?.updateFinSwimResult?.__typename === 'ErrorResponse') {
                const errorResponse = data.updateFinSwimResult;
                const specificErrorField = errorResponse.fields?.find(f => f.message);
                const errorMessage = specificErrorField?.message || t('updateError.description', { type: 'FIN Swim' });

                showErrorNotification({
                    message: t('updateError.title', { type: 'FIN Swim' }),
                    description: errorMessage,
                });

                throw new Error(errorMessage);
            } else {
                showSuccessNotification({
                    message: t('updateSuccess.title', { type: 'FIN Swim' }),
                    description: t('updateSuccess.message', { type: 'FIN Swim' }),
                });

                onClose(true);
            }
        } catch (error) {
            console.error('Error updating FIN Swim performance result:', error);
            throw error;
        }
    };

    const modalFooter = (
        <div className="mt-5 flex w-full justify-end">
            <div className="flex space-x-2">
                <Button variant="secondary" onClick={() => onClose()}>
                    {t('finSwim.editModal.cancelBtn')}
                </Button>
                <Button variant="primary" onClick={handleSubmit}>
                    {t('finSwim.editModal.saveBtn')}
                </Button>
            </div>
        </div>
    );

    return (
        <FormProvider {...form}>
            <Modal
                open={open}
                onClose={onClose}
                title={t('finSwim.editModal.title')}
                footer={modalFooter}
                showCloseButton={false}
                className="w-full sm:w-xl"
                largeTitle
            >
                <form onSubmit={handleSubmit}>
                    <div className="grid gap-4 sm:grid-cols-3">
                        <div className="sm:col-span-3">
                            <DatePickerField name="date" label={t('finSwim.form.date')} required />
                        </div>
                        <FinSwimInnerForm />
                        <div className="sm:col-span-3">
                            <SwitchField
                                name="isTiedToAssessmentRun"
                                label={t('soc.form.tieToAssessmentRun', {
                                    defaultValue: 'Tie To Assessment Run',
                                })}
                            />
                        </div>
                        {isTiedToAssessmentRun && (
                            <div className="sm:col-span-3">
                                <SelectField
                                    name="assessmentRunId"
                                    label={t('filter.assessmentRun')}
                                    placeholder={t('filter.assessmentRunPlaceholder')}
                                    options={assessmentRunOptions}
                                    required
                                />
                            </div>
                        )}
                    </div>
                </form>
            </Modal>
        </FormProvider>
    );
};

export default EditPerformanceFinSwimModal;
